import { useState, useMemo, useCallback } from 'react';
import { DataTable } from '@/components/common';
import { getOfferColumns } from './OfferTable.columns';
import type { Offer } from '../../types';

interface OfferTableProps {
  data: Offer[];
  isLoading?: boolean;
  error?: Error | null;
  renderEditForm?: (
    offer: Offer,
    onClose: () => void
  ) => React.ReactNode;
  onViewDetails?: (offer: Offer) => void;
}

export function OfferTable({
  data,
  isLoading = false,
  error = null,
  renderEditForm,
  onViewDetails,
}: OfferTableProps) {
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  
  const handleEdit = useCallback((offer: Offer) => {
    setEditingOffer(offer);
  }, []);
  
  const columns = useMemo(
    () => getOfferColumns({ 
      onEdit: renderEditForm ? handleEdit : undefined,
      onViewDetails 
    }), 
    [handleEdit, renderEditForm, onViewDetails]
  );

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading offers...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive mb-2">Failed to load offers</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  // Handle empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
        <div className="text-center">
          <p className="text-lg font-medium mb-2">No offers found</p>
          <p className="text-sm text-muted-foreground">
            Create your first offer to get started
          </p>
        </div>
      </div>
    );
  }

  return (
    <DataTable
      data={data}
      columns={columns}
      editingItem={editingOffer}
      onEditingItemChange={setEditingOffer}
      renderEditForm={renderEditForm}
      config={{
        enableColumnVisibility: true,
        enableSorting: true,
        enableFiltering: true,
        defaultPageSize: 10,
        pageSizeOptions: [5, 10, 20, 50],
        editDrawerTitle: 'Edit Offer',
        editDrawerDescription: 'Update offer information',
        searchPlaceholder: 'Search offers...',
      }}
      testIdPrefix="offers-table"
    />
  );
}